import dayjs from 'dayjs'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo } from 'react'
import { v4 } from 'uuid'
import { z } from 'zod'

const stringOrArraySchema = z
  .union([z.string(), z.array(z.string())])
  .transform((val) => {
    const arr = Array.isArray(val) ? val : [val]
    // Remove duplicates while preserving order
    return [...new Set(arr)] as ReadonlyArray<string>
  })

const now = () => dayjs().format('YYYY-MM-DD')

export const niiSourcesParamsSchema = z.object({
  id: z.string().default(v4().substring(0, 6)),
  commission_date: z.string().optional(),
  design_code: z.string().optional(),
  contents: z.string().optional(),
  external_coating: z.string().optional(),
  insulation: z.string().optional(),
  design_pressure_bar: z.number().optional(),
  operating_pressure_bar: z.number().optional(),
  design_temperature_c: z.number().optional(),
  max_operating_temperature_c: z.number().optional(),
  internal_inspection_interval_yrs: z.number().optional(),
  next_internal_inspection: z.string().optional(),
})

export const niiSourcesZoneParamsSchema = z.object({
  id: z.string().default(v4().substring(0, 6)),
  title: z.string().default('Untitled Zone'),
  material: z.string().optional(),
  geometry: z.string().optional(),
  length_mm: z.number().optional(),
  height_mm: z.number().optional(),
  internal_diameter_mm: z.number().optional(),
  external_diameter_mm: z.number().optional(),
  total_surface_area_sq_m: z.number().optional(),
  wall_thickness_shell_mm: z.number().optional(),
  wall_thickness_heads_mm: z.number().optional(),
  corrosion_allowance_mm: z.number().optional(),
})

const coverageSchema = z.object({
  coverage_percentage: z.number(),
  coverage_area: z.number(),
})

export const eligibilityValues = [
  'eligible',
  'not_eligible',
  'unclear',
] as const
export const niiReportTicketPropsSchema = z
  .object({
    analysisDate: z.iso.date().default(now),
    justification: z.string().optional(),
    inspectionNotes: z.string().optional(),
    eligibility: z.enum(eligibilityValues).default('unclear'),
    functionalLocation: z.string().optional(),
    params: niiSourcesParamsSchema.optional(),
    zoneParams: z.array(niiSourcesZoneParamsSchema).optional(),
    analysisResults: z.record(z.string(), z.string()).optional(),
    recommendations: z.array(z.string()).readonly().default([]),
    coverage: coverageSchema.optional(),
  })
  .readonly()

export type NiiSourcesParams = z.infer<typeof niiSourcesParamsSchema>
export type NiiSourcesZoneParams = z.infer<typeof niiSourcesZoneParamsSchema>
export type NiiReportTicketProps = z.infer<typeof niiReportTicketPropsSchema>

export type NiiReportField = keyof NiiReportTicketProps

export const niiReportConfigSchema = z.object({
  referenceFields: z
    .partialRecord(
      niiReportTicketPropsSchema.unwrap().keyof(),
      stringOrArraySchema
    )
    .optional(),
})

export type NiiReportConfig = z.infer<typeof niiReportConfigSchema>

export const TICKET_PROPS_NII_DATA_KEY = 'niiData'

export function useSaveTicketNiiData(): (
  niiData: NiiReportTicketProps
) => Promise<void> {
  const { ticketProps, updateTicketProps } = useTicketUpdate()
  return useCallback(
    async (niiData: NiiReportTicketProps) => {
      const newProps = { ...ticketProps, [TICKET_PROPS_NII_DATA_KEY]: niiData }
      await updateTicketProps(newProps)
    },
    [ticketProps, updateTicketProps]
  )
}

export function useTicketNiiData(): {
  isLoading: boolean
  niiData?: NiiReportTicketProps
} {
  const { ticketProps, isLoading } = useTicketUpdate()
  const { errorNotification } = useGeckoNotifications()
  return useMemo(() => {
    const niiData = niiReportTicketPropsSchema.safeParse(
      ticketProps[TICKET_PROPS_NII_DATA_KEY] ?? {}
    )
    if (!niiData.success) {
      errorNotification('Invalid NII data on ticket')
      return {
        isLoading,
        niiData: undefined,
      }
    }
    return {
      isLoading,
      niiData: niiData.success ? niiData.data : undefined,
    }
  }, [ticketProps, isLoading, errorNotification])
}
